import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Card, Button, Alert } from 'react-bootstrap';
import StudentEmploymentRequirements from './StudentEmploymentRequirements';
import { MEETING_HOUR_OPTIONS } from './meetings';

// Qualtrics LAQ5: "How will LA support be used? (click all that apply)" —
// these thirteen values must stay in sync with the enum on
// server/src/api/application/application.model.js's
// programSpecificResponses.la.supportUseCases (that file's T-AM-14 test
// pins the same set, so a rename on either side without the other fails a
// test instead of silently drifting).
export const LA_SUPPORT_USE_CASE_OPTIONS = [
    { value: 'assisting_lecture', label: 'Assisting in lecture' },
    { value: 'assisting_lab', label: 'Assisting in lab' },
    { value: 'office_hours', label: 'Leading/assisting office hours' },
    { value: 'feedback', label: 'Providing feedback to students' },
    { value: 'rapport', label: 'Building rapport with students' },
    {
        value: 'belonging',
        label: 'Creating a sense of belonging with students',
    },
    {
        value: 'learning_strategies',
        label: 'Implementing and teaching learning strategies',
    },
    { value: 'small_group_collaboration', label: 'Small group collaboration' },
    { value: 'growth_mindset', label: 'Promoting growth mindset' },
    {
        value: 'edi_teaching_strategies',
        label: 'Implementing equity, diversity, and inclusive teaching strategies',
    },
    {
        value: 'questioning_understanding',
        label: "Questioning student's understanding",
    },
    { value: 'metacognition', label: 'Promoting student metacognition' },
    { value: 'other', label: 'Other' },
];

// O(1) label lookup for read-only views, built once from the array above
// instead of a .find() per rendered value.
export const LA_SUPPORT_USE_CASE_LABELS = new Map(
    LA_SUPPORT_USE_CASE_OPTIONS.map((opt) => [opt.value, opt.label])
);

// Qualtrics LAQ4: "If your nominee declines?"
export const LA_NOMINEE_DECLINE_OPTIONS = [
    { value: 'open_to_applicants', label: 'I want potential LAs to apply' },
    { value: 'no_la', label: 'I do not want an LA' },
];

// Qualtrics LAQ3: "Will you be naming a student for this role?" Answer
// wording from Angela (LA program), 2026-09-25 — keep it verbatim.
// The 900 number field of the LA nominee at a position in the list
export const nomineeIdFieldFor = (position) =>
    `ca-la-nominee-${position}-studentId`;

export const LA_NOMINEE_STATUS_OPTIONS = [
    { value: 'selected', label: 'Yes, I have someone selected' },
    { value: 'selecting', label: "Yes, I'm working on selecting someone" },
    {
        value: 'needs_help',
        label: 'No, I would like your help identifying someone',
    },
];

// The requirements now live with the box that shows them; re-exported so
// existing imports keep working
export {
    STUDENT_EMPLOYMENT_REQUIREMENTS,
    STUDENT_EMPLOYMENT_REQUIREMENTS_URL,
} from './StudentEmploymentRequirements';

// The first two LA nominees that are the same student, by 900-number or
// email (ignoring case), as positions; null when they all differ. Blank
// fields don't match each other. The server's laNomineesRefusal applies
// the same rule.
export function findDuplicateLANominee(nominees) {
    const key = (value) => value?.trim().toLowerCase() || null;
    for (let later = 1; later < nominees.length; later++) {
        for (let earlier = 0; earlier < later; earlier++) {
            const a = nominees[earlier];
            const b = nominees[later];
            if (
                (key(a.studentId) && key(a.studentId) === key(b.studentId)) ||
                (key(a.email) && key(a.email) === key(b.email))
            ) {
                return [earlier, later];
            }
        }
    }
    return null;
}

// LAQ9's recruiting message is needed whenever some requested LA has no
// nominee: no nominee at all (needs_help), fewer nominees than LAs
// requested, or nominees who might decline with the course then opened to
// applicants. Never while the faculty member is still selecting someone
// themselves. laNomineeCount is the number of 'la' namedStudents entries.
// The server's needsLARecruitingMessage applies the same rule. It decides
// only whether the field is shown and required: the form keeps the text
// either way, so changing an answer back doesn't lose it, and the server
// drops a message no longer asked for on submit.
export function needsLARecruitingMessage(la, laNomineeCount) {
    if (la.nomineeStatus === 'selected') {
        return (
            la.nomineeDeclinePreference === 'open_to_applicants' ||
            laNomineeCount < (Number(la.requestedLACount) || 0)
        );
    }
    return la.nomineeStatus !== 'selecting';
}

// Mirrors the shape of Application.programSpecificResponses in
// server/src/api/application/application.model.js — the default state for
// both the create wizard and the edit/resubmit flow. A factory (rather than
// a shared object literal) so each component gets its own fresh
// object/array, same reasoning as CreateApplicationSection4.js's
// getDefaultCourseContext.
export function getDefaultProgramResponses() {
    return {
        ta: {
            chairApprovalObtained: false,
            expectedResponsibilities: '',
            acknowledgesLAExclusion: false,
        },
        la: {
            // LAQ3: one of LA_NOMINEE_STATUS_OPTIONS' values. null means
            // "not yet answered" so the question can actually be required.
            nomineeStatus: null,
            expectedLAResponsibilities: '',
            weeklyPrepPlan: '',
            assessmentPlan: '',
            acknowledgesContract: false,
            acknowledgesLARequirements: false,
            requestedLACount: '',
            nomineeDeclinePreference: '',
            supportUseCases: [],
            otherSupportUseCase: '',
            weeklyMeetingHours: '',
            recruitingMessage: '',
        },
        si: {
            isBottleneckCourse: false,
            expectedSessionsPerWeek: '',
            instructorWillingToCollaborate: false,
        },
    };
}

// Saved programSpecificResponses merged onto the defaults, for loading an
// application back into the form (ApplicationDetailPage's edit flow and
// duplicating, #457)
export function programResponsesFromSaved(saved) {
    const defaults = getDefaultProgramResponses();
    const savedLA = saved?.la || {};
    return {
        si: { ...defaults.si, ...(saved?.si || {}) },
        ta: { ...defaults.ta, ...(saved?.ta || {}) },
        la: {
            ...defaults.la,
            ...savedLA,
            // Number fields round-trip through Mongoose as null when unset
            // (an empty string cast on save), not as an absent key —
            // normalize back to '' so the form can't render the literal
            // string "null" or hand a select/range input a null value.
            requestedLACount: savedLA.requestedLACount ?? '',
            weeklyMeetingHours: savedLA.weeklyMeetingHours ?? '',
        },
    };
}

// Single source of truth for "what's missing from the Program
// Questionnaire," shared by CreateApplication.js's and
// ApplicationDetailPage.js's canProceed/error-summary so the create wizard
// and the edit/resubmit flow can't silently diverge on what counts as a
// valid submission. Every LA question is required — mirrors
// application.controller.js's isProgramQuestionnaireComplete on the server
// (can't share the function across the client/server boundary, but the
// rules it enforces are the same). Same submit-time-only pattern as
// getCourseContextErrors: not schema-required, so a draft can still be
// saved incomplete. The TA-requires-a-named-student rule below is enforced
// only here, client-side (known gap, see application.model.js's
// namedStudents field comment).
//
// Collects every applicable error instead of stopping at the first one, so
// a blocked advance can summarize everything left to fix rather than one
// field at a time. All ids are fixed 'ca-' ids (this component isn't
// parameterized by idPrefix — the create and detail pages never mount it at
// the same time).
export function getProgramQuestionnaireErrors(
    namedStudents,
    programResponses,
    selectedPrograms
) {
    const errors = [];
    const selectedKeys = new Set(selectedPrograms.map((p) => p.key));

    if (selectedKeys.has('ta')) {
        const taStudents = namedStudents
            .map((student, index) => ({ student, index }))
            .filter(({ student }) => student.forProgram === 'ta');

        if (taStudents.length === 0) {
            errors.push({
                id: 'ca-add-ta-student-btn',
                label: 'At least one named TA student',
            });
        } else {
            taStudents.forEach(({ student, index }, position) => {
                if (!student.name.trim()) {
                    errors.push({
                        id: `ca-student-${index}-name`,
                        label: `TA student ${position + 1} name`,
                    });
                }
                if (!student.email.trim()) {
                    errors.push({
                        id: `ca-student-${index}-email`,
                        label: `TA student ${position + 1} email`,
                    });
                }
            });
        }
    }

    if (selectedKeys.has('la')) {
        const la = programResponses.la;

        // Pushed in the same order the component below renders these
        // fields, so both the first-error focus target and the "Missing:
        // ..." summary read top-to-bottom instead of jumping around.
        if (la.requestedLACount === '' || la.requestedLACount == null) {
            errors.push({
                id: 'ca-requested-la-count',
                label: 'Number of LAs requested',
            });
        }

        // LAQ3: must be explicitly answered — null means "not yet
        // answered."
        if (
            !LA_NOMINEE_STATUS_OPTIONS.some(
                (opt) => opt.value === la.nomineeStatus
            )
        ) {
            errors.push({
                id: 'ca-la-nominee-group',
                label: 'LA nominee selection',
            });
        }

        // LAQ3.1's nominees aren't a stored field on their own — they're
        // the 'la'-tagged namedStudents entries, which NamedStudentSchema
        // already requires name+email+900# for. Applying the same rule to
        // every row here (mirroring the TA check above) keeps the client
        // from letting a half-filled nominee through to a server 400. Ids
        // use the row's position among the LA entries, the same ids
        // SelectedStudentsFields renders.
        //
        // Gated on nomineeStatus === 'selected' (not just entries existing)
        // to match exactly what the component below renders — nomineeStatus
        // and the entries are normally kept in sync by
        // handleLANomineeChange, but data loaded from the server (an older
        // or directly-API-created application) isn't guaranteed to agree.
        // Checking stale entries regardless of nomineeStatus would flag
        // fields the component never mounts.
        const hasLANominee = la.nomineeStatus === 'selected';
        const laNominees = namedStudents.filter((s) => s.forProgram === 'la');
        if (hasLANominee && laNominees.length > 0) {
            laNominees.forEach((nominee, position) => {
                const id = `ca-la-nominee-${position}`;
                const label = `LA nominee ${position + 1}`;
                if (!nominee.name?.trim()) {
                    errors.push({ id: `${id}-name`, label: `${label} name` });
                }
                if (!nominee.email?.trim()) {
                    errors.push({ id: `${id}-email`, label: `${label} email` });
                }
                if (!nominee.studentId?.trim()) {
                    errors.push({
                        id: `${id}-studentId`,
                        label: `${label} 900 number`,
                    });
                }
            });
            // In the order SelectedStudentsFields shows them: the duplicate
            // error right under the rows, then the count error
            if (findDuplicateLANominee(laNominees)) {
                errors.push({
                    id: 'ca-la-nominee-duplicate-error',
                    label: 'The same student named twice',
                });
            }
            // Lowering the LA count below the number of rows already named
            if (
                la.requestedLACount &&
                laNominees.length > Number(la.requestedLACount)
            ) {
                errors.push({
                    id: 'ca-la-nominee-count-error',
                    label: 'More LA nominees than LAs requested',
                });
            }
            // LAQ4 only appears once a nominee exists, but it's still an
            // answer coordinators need — a nominee with no stated decline
            // preference is as incomplete as one with a blank name.
            if (!la.nomineeDeclinePreference) {
                errors.push({
                    id: 'ca-la-decline-group',
                    label: 'Nominee decline preference',
                });
            }
        }

        if (!la.supportUseCases?.length) {
            errors.push({
                id: 'ca-la-support-use-cases-group',
                label: 'LA support use cases',
            });
        }
        if (
            la.supportUseCases?.includes('other') &&
            !la.otherSupportUseCase?.trim()
        ) {
            errors.push({
                id: 'ca-la-other-support-use-case',
                label: 'LA other use case description',
            });
        }
        if (!la.expectedLAResponsibilities?.trim()) {
            errors.push({
                id: 'ca-expected-la-responsibilities',
                label: 'Weekly LA usage explanation',
            });
        }
        if (!la.weeklyPrepPlan?.trim()) {
            errors.push({
                id: 'ca-weekly-prep-plan',
                label: 'Weekly prep plan',
            });
        }
        // A required weekly prep session can't be 0 hours — treat the
        // slider's own default/untouched position the same as blank.
        if (!la.weeklyMeetingHours) {
            errors.push({
                id: 'ca-weekly-meeting-hours',
                label: 'Weekly meeting hours',
            });
        }
        if (!la.assessmentPlan?.trim()) {
            errors.push({
                id: 'ca-assessment-plan',
                label: 'LA assessment plan',
            });
        }

        if (
            needsLARecruitingMessage(la, laNominees.length) &&
            !la.recruitingMessage?.trim()
        ) {
            errors.push({
                id: 'ca-recruiting-message',
                label: 'Recruiting message',
            });
        }
    }

    return errors;
}

// Boolean form of getProgramQuestionnaireErrors, for callers that only need
// "is this complete," not the error detail. Both wizard pages now call
// getProgramQuestionnaireErrors directly (they need the detail), so this
// has no production caller today — kept for the dedicated unit tests that
// pin the rule independently of the UI (CreateApplication.test.js).
export function isProgramQuestionnaireComplete(
    namedStudents,
    programResponses,
    selectedPrograms
) {
    return (
        getProgramQuestionnaireErrors(
            namedStudents,
            programResponses,
            selectedPrograms
        ).length === 0
    );
}

// LAQ2: "Your student-to-LA ratio (auto-generated enrollment/#LAs)" —
// display only, never persisted. Uses the section's self-reported
// courseConfirmation.enrollment (what faculty actually entered for their
// section), not the course catalog's aggregate enrollment.capacity — a
// faculty member's specific section can enroll well below (or above) the
// catalog figure (#436). Returns null when there isn't enough information
// yet to compute it. Exported so ApplicationDetailPage.js's read-only
// detail view (which doesn't render this component) computes the same
// ratio the same way.
export function computeLARatio(courseConfirmation, requestedLACount) {
    const enrollment = courseConfirmation?.enrollment;
    if (!enrollment || !requestedLACount) return null;
    return Math.round(enrollment / requestedLACount);
}

export function SelectedStudentsFields({
    nominees,
    requestedCount,
    onAdd,
    onUpdate,
    onRemove,
    hasError,
    // The nominee panel on a submitted application can take every row away;
    // in the wizard "selected" means at least one nominee
    allowRemovingLast = false,
    // The server's refusal of a nominee's 900 number, by field id
    nomineeIdErrors = new Map(),
    // Once submitted, the number of LAs requested is no longer the faculty
    // member's to change, so the count error only says to remove one
    canRaiseCount = true,
}) {
    const [focusId, setFocusId] = useState(null);
    useEffect(() => {
        if (focusId) {
            document.getElementById(focusId)?.focus();
            setFocusId(null);
        }
    }, [focusId]);

    // What shows under the rows for a number of rows: the hint, the count
    // error, Add, or the all-named message. Each can take focus, so a
    // removed row's focus always has somewhere to go.
    const belowRowsId = (rows) =>
        !requestedCount
            ? 'ca-la-nominee-count-hint'
            : rows > requestedCount
              ? 'ca-la-nominee-count-error'
              : rows < requestedCount
                ? 'ca-add-la-nominee'
                : 'ca-la-nominee-count-status';
    const duplicate = findDuplicateLANominee(
        nominees.map(({ student }) => student)
    );
    const showCountError = hasError('ca-la-nominee-count-error');

    return (
        <>
            {nominees.map(({ student, index }, position) => {
                const id = `ca-la-nominee-${position}`;
                // A fieldset per row, so a screen reader announces each
                // input with its nominee ("Nominee 2, Student Name") instead
                // of three identical "Student Name" fields
                return (
                    <fieldset key={index}>
                        <legend className="fs-6 fw-semibold mb-2">
                            Nominee {position + 1}
                        </legend>
                        <Row>
                            <Col md={4}>
                                <Form.Group
                                    className="mb-3"
                                    controlId={`${id}-name`}
                                >
                                    <Form.Label>
                                        Student Name{' '}
                                        <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={student.name}
                                        onChange={(e) =>
                                            onUpdate(
                                                index,
                                                'name',
                                                e.target.value
                                            )
                                        }
                                        required
                                        isInvalid={hasError(`${id}-name`)}
                                        aria-invalid={hasError(`${id}-name`)}
                                        aria-describedby={
                                            hasError(`${id}-name`)
                                                ? `${id}-name-error`
                                                : undefined
                                        }
                                    />
                                    {hasError(`${id}-name`) && (
                                        <Form.Control.Feedback
                                            type="invalid"
                                            id={`${id}-name-error`}
                                            role="alert"
                                        >
                                            This field is required.
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group
                                    className="mb-3"
                                    controlId={`${id}-email`}
                                >
                                    <Form.Label>
                                        Student Email{' '}
                                        <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={student.email}
                                        onChange={(e) =>
                                            onUpdate(
                                                index,
                                                'email',
                                                e.target.value
                                            )
                                        }
                                        required
                                        isInvalid={hasError(`${id}-email`)}
                                        aria-invalid={hasError(`${id}-email`)}
                                        aria-describedby={
                                            hasError(`${id}-email`)
                                                ? `${id}-email-error`
                                                : undefined
                                        }
                                    />
                                    {hasError(`${id}-email`) && (
                                        <Form.Control.Feedback
                                            type="invalid"
                                            id={`${id}-email-error`}
                                            role="alert"
                                        >
                                            This field is required.
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group
                                    className="mb-3"
                                    controlId={`${id}-studentId`}
                                >
                                    <Form.Label>
                                        Student 900 number{' '}
                                        <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="900xxxxxx"
                                        value={student.studentId}
                                        onChange={(e) =>
                                            onUpdate(
                                                index,
                                                'studentId',
                                                e.target.value
                                            )
                                        }
                                        required
                                        isInvalid={hasError(`${id}-studentId`)}
                                        aria-invalid={hasError(
                                            `${id}-studentId`
                                        )}
                                        aria-describedby={
                                            hasError(`${id}-studentId`)
                                                ? `${id}-studentId-error`
                                                : undefined
                                        }
                                    />
                                    {hasError(`${id}-studentId`) && (
                                        <Form.Control.Feedback
                                            type="invalid"
                                            id={`${id}-studentId-error`}
                                            role="alert"
                                        >
                                            {nomineeIdErrors.get(
                                                `${id}-studentId`
                                            ) ?? 'This field is required.'}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            {/* Any row, as long as one stays unless
                            allowRemovingLast */}
                            {(allowRemovingLast || nominees.length > 1) && (
                                <div className="text-end mt-2">
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => {
                                            onRemove(index);
                                            setFocusId(
                                                belowRowsId(nominees.length - 1)
                                            );
                                        }}
                                        aria-label={`Remove nominee ${position + 1}`}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            )}
                        </Row>
                    </fieldset>
                );
            })}
            {/* Like every other field error, only once the faculty member
                tries to leave the section or submit */}
            {duplicate && hasError('ca-la-nominee-duplicate-error') && (
                <p
                    id="ca-la-nominee-duplicate-error"
                    className="text-danger small mb-3"
                    role="alert"
                    // Focus target for the error summary
                    tabIndex={-1}
                >
                    Nominees {duplicate[0] + 1} and {duplicate[1] + 1} are the
                    same student (same 900# or email). Name a different student,
                    or remove one of them.
                </p>
            )}
            {/* One of four states: no count chosen yet, more rows than LAs
                requested (the count was lowered), room for another row, or
                a nominee for every LA requested. More rows than requested
                says so as a plain note until the error shows, the way the
                other errors wait; the keys make the error a new element, so
                it's announced when it appears */}
            {!requestedCount ? (
                <p
                    id="ca-la-nominee-count-hint"
                    className="text-muted small mb-3"
                    tabIndex={-1}
                >
                    Choose how many LAs you&apos;re requesting to name more than
                    one nominee.
                </p>
            ) : nominees.length > requestedCount ? (
                <p
                    key={showCountError ? 'count-error' : 'count-note'}
                    // Focus target for the error summary, and for Remove
                    // while still over the count
                    id="ca-la-nominee-count-error"
                    className={`${
                        showCountError ? 'text-danger' : 'text-muted'
                    } small mb-3`}
                    role={showCountError ? 'alert' : undefined}
                    tabIndex={-1}
                >
                    You&apos;ve named {nominees.length} nominees but requested{' '}
                    {requestedCount} {requestedCount === 1 ? 'LA' : 'LAs'}.{' '}
                    {canRaiseCount
                        ? "Remove a nominee, or raise the number of LAs you're requesting."
                        : 'Remove a nominee.'}
                </p>
            ) : nominees.length < requestedCount ? (
                <Button
                    id="ca-add-la-nominee"
                    className="mb-3"
                    variant="outline-primary"
                    size="sm"
                    onClick={() => {
                        onAdd();
                        // The new row's position is the current length
                        setFocusId(`ca-la-nominee-${nominees.length}-name`);
                    }}
                >
                    + Add another nominee
                </Button>
            ) : (
                <p
                    id="ca-la-nominee-count-status"
                    className="text-muted small mb-3"
                    tabIndex={-1}
                >
                    You&apos;ve named a nominee for each LA you requested.
                </p>
            )}
        </>
    );
}

// Section 5: Program Questionnaire
function CreateApplicationSection5({
    namedStudents,
    addNamedStudent,
    updateNamedStudent,
    removeNamedStudent,
    selectedPrograms,
    programResponses,
    onProgramResponseChange,
    hasTA,
    courseConfirmation,
    // null while the LA requirements aren't loaded
    laTrainingSessions = null,
    errorIds = new Set(),
    // The server's refusal of an LA nominee's 900 number, by field id, shown
    // at the field
    nomineeIdErrors = new Map(),
}) {
    const hasError = (id) => errorIds.has(id) || nomineeIdErrors.has(id);
    const selected = selectedPrograms.map((p) => p.key);
    const hasLA = selected.includes('la');

    // namedStudents is one shared array across programs (forProgram tags
    // each entry) — filter to this section's slice but keep the original
    // index, since update/removeNamedStudent address the full array.
    const taStudents = namedStudents
        .map((student, index) => ({ student, index }))
        .filter(({ student }) => student.forProgram === 'ta');

    // LAQ3 ("will you be naming a student?") is its own field
    // (programResponses.la.nomineeStatus, null until answered) — driven by
    // nomineeStatus rather than mere namedStudents presence so no radio
    // shows pre-checked before the faculty member actually picks one.
    // nomineeStatus and the 'la'-tagged namedStudents entries are kept in
    // sync by handleLANomineeChange below. Faculty can name one nominee
    // per LA requested (#557): choosing "selected" adds the first row and
    // SelectedStudentsFields adds the rest, up to requestedLACount.
    const nomineeStatus = programResponses.la.nomineeStatus;
    const hasLANominee = nomineeStatus === 'selected';
    // Training a nominee may have to attend (#404). Returning-only sessions
    // are left out; new-only ones say so.
    const nomineeSessions = (laTrainingSessions || []).filter(
        (t) => t.appliesTo !== 'returning'
    );
    const trainingReminder = nomineeSessions.length
        ? `Make sure your student can attend LA training: ${nomineeSessions
              .map(
                  (t) =>
                      `${t.title}, ${t.when.replace(/\.$/, '')}${
                          t.appliesTo === 'new' ? ' (new LAs)' : ''
                      }`
              )
              .join('; ')}.`
        : 'If your student is a new LA, make sure they can attend LA training.';
    const laNominees = namedStudents
        .map((student, index) => ({ student, index }))
        .filter(({ student }) => student.forProgram === 'la');

    const toggleLASupportUseCase = (optionValue) => {
        const current = programResponses.la.supportUseCases || [];
        const isSelected = current.includes(optionValue);
        const next = isSelected
            ? current.filter((v) => v !== optionValue)
            : [...current, optionValue];
        onProgramResponseChange('la', 'supportUseCases', next);
        // Clear the stale free-text answer once "Other" is deselected,
        // since the field is hidden (and no longer meaningful) after this.
        if (optionValue === 'other' && isSelected) {
            onProgramResponseChange('la', 'otherSupportUseCase', '');
        }
    };

    // The recruiting message is never cleared here: needsLARecruitingMessage
    // only hides it, so changing the answer back still shows what was typed
    const handleLANomineeChange = (nextStatus) => {
        onProgramResponseChange('la', 'nomineeStatus', nextStatus);
        if (nextStatus === 'selected') {
            if (laNominees.length === 0) {
                addNamedStudent('la');
            }
            return;
        }
        if (laNominees.length > 0) {
            [...laNominees].reverse().forEach((entry) => {
                removeNamedStudent(entry.index);
            });
            onProgramResponseChange('la', 'nomineeDeclinePreference', '');
        }
    };

    const handleAddLANominee = () => {
        addNamedStudent('la');
    };

    const handleRequestedLACountChange = (value) => {
        onProgramResponseChange('la', 'requestedLACount', value);
    };

    const handleNomineeDeclinePreferenceChange = (value) => {
        onProgramResponseChange('la', 'nomineeDeclinePreference', value);
    };

    const laRatio = computeLARatio(
        courseConfirmation,
        programResponses.la.requestedLACount
    );
    const needsRecruitingMessage = needsLARecruitingMessage(
        programResponses.la,
        laNominees.length
    );

    return (
        <div>
            <h2 className="h5 mb-3">Program Details</h2>
            <p className="text-muted mb-3">
                Answer additional questions for each program you selected.
            </p>

            {hasTA && (
                <div className="mb-4">
                    <h3 className="h6 mb-3">Named Students</h3>
                    <Alert variant="warning">
                        The TA program requires you to identify at least one
                        student for consideration. Please provide their name,
                        MSU Denver email, and optionally their 900 number.
                    </Alert>
                    {hasError('ca-add-ta-student-btn') && (
                        <p className="text-danger small" role="alert">
                            Add at least one named TA student.
                        </p>
                    )}
                    {taStudents.map(({ student, index }, position) => (
                        <Card key={index} className="mb-3 border-0 shadow-sm">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <p className="mb-0 fw-semibold">
                                        Student {position + 1}
                                    </p>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() =>
                                            removeNamedStudent(index)
                                        }
                                    >
                                        Remove
                                    </Button>
                                </div>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group
                                            className="mb-3"
                                            controlId={`ca-student-${index}-name`}
                                        >
                                            <Form.Label>
                                                Name{' '}
                                                <span className="text-danger">
                                                    *
                                                </span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Student full name"
                                                value={student.name}
                                                onChange={(e) =>
                                                    updateNamedStudent(
                                                        index,
                                                        'name',
                                                        e.target.value
                                                    )
                                                }
                                                required
                                                isInvalid={hasError(
                                                    `ca-student-${index}-name`
                                                )}
                                                aria-invalid={hasError(
                                                    `ca-student-${index}-name`
                                                )}
                                                aria-describedby={
                                                    hasError(
                                                        `ca-student-${index}-name`
                                                    )
                                                        ? `ca-student-${index}-name-error`
                                                        : undefined
                                                }
                                            />
                                            {hasError(
                                                `ca-student-${index}-name`
                                            ) && (
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                    id={`ca-student-${index}-name-error`}
                                                    role="alert"
                                                >
                                                    This field is required.
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group
                                            className="mb-3"
                                            controlId={`ca-student-${index}-email`}
                                        >
                                            <Form.Label>
                                                Email{' '}
                                                <span className="text-danger">
                                                    *
                                                </span>
                                            </Form.Label>
                                            <Form.Control
                                                type="email"
                                                placeholder="student@msudenver.edu"
                                                value={student.email}
                                                onChange={(e) =>
                                                    updateNamedStudent(
                                                        index,
                                                        'email',
                                                        e.target.value
                                                    )
                                                }
                                                required
                                                isInvalid={hasError(
                                                    `ca-student-${index}-email`
                                                )}
                                                aria-invalid={hasError(
                                                    `ca-student-${index}-email`
                                                )}
                                                aria-describedby={
                                                    hasError(
                                                        `ca-student-${index}-email`
                                                    )
                                                        ? `ca-student-${index}-email-error`
                                                        : undefined
                                                }
                                            />
                                            {hasError(
                                                `ca-student-${index}-email`
                                            ) && (
                                                <Form.Control.Feedback
                                                    type="invalid"
                                                    id={`ca-student-${index}-email-error`}
                                                    role="alert"
                                                >
                                                    This field is required.
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group
                                            className="mb-3"
                                            controlId={`ca-student-${index}-studentId`}
                                        >
                                            <Form.Label>900 number</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="900xxxxxx"
                                                value={student.studentId}
                                                onChange={(e) =>
                                                    updateNamedStudent(
                                                        index,
                                                        'studentId',
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    ))}
                    <Button
                        id="ca-add-ta-student-btn"
                        variant="outline-primary"
                        onClick={() => addNamedStudent('ta')}
                    >
                        + Add Student
                    </Button>
                </div>
            )}

            {hasTA && (
                <Card className="mb-4 border-0 shadow-sm">
                    <Card.Header className="bg-primary text-white">
                        <strong>TA - Teaching Assistant</strong>
                    </Card.Header>
                    <Card.Body>
                        <Form.Check
                            type="checkbox"
                            id="ca-chair-approval"
                            label="Department Chair approval has been obtained"
                            className="mb-3"
                            checked={programResponses.ta.chairApprovalObtained}
                            onChange={(e) =>
                                onProgramResponseChange(
                                    'ta',
                                    'chairApprovalObtained',
                                    e.target.checked
                                )
                            }
                        />
                        <Form.Group
                            className="mb-3"
                            controlId="ca-expected-ta-responsibilities"
                        >
                            <Form.Label>
                                Expected TA Responsibilities
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="What will the TA be expected to do?"
                                value={
                                    programResponses.ta.expectedResponsibilities
                                }
                                onChange={(e) =>
                                    onProgramResponseChange(
                                        'ta',
                                        'expectedResponsibilities',
                                        e.target.value
                                    )
                                }
                            />
                        </Form.Group>
                        <Form.Check
                            type="checkbox"
                            id="ca-la-exclusion"
                            label="I understand that a course may not receive both an LA and a TA"
                            checked={
                                programResponses.ta.acknowledgesLAExclusion
                            }
                            onChange={(e) =>
                                onProgramResponseChange(
                                    'ta',
                                    'acknowledgesLAExclusion',
                                    e.target.checked
                                )
                            }
                        />
                    </Card.Body>
                </Card>
            )}

            {hasLA && (
                <Card className="mb-4 border-0 shadow-sm">
                    <Card.Header className="badge-la">
                        <strong>LA - Learning Assistant</strong>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group
                                    className="mb-3"
                                    controlId="ca-requested-la-count"
                                >
                                    <Form.Label>
                                        How many LAs are you requesting?{' '}
                                        <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={
                                            programResponses.la
                                                .requestedLACount ?? ''
                                        }
                                        onChange={(e) =>
                                            handleRequestedLACountChange(
                                                e.target.value
                                                    ? Number(e.target.value)
                                                    : ''
                                            )
                                        }
                                        required
                                        isInvalid={hasError(
                                            'ca-requested-la-count'
                                        )}
                                        aria-invalid={hasError(
                                            'ca-requested-la-count'
                                        )}
                                        aria-describedby={
                                            hasError('ca-requested-la-count')
                                                ? 'ca-requested-la-count-error'
                                                : undefined
                                        }
                                    >
                                        <option value="">Select...</option>
                                        <option value={1}>1</option>
                                        <option value={2}>2</option>
                                        <option value={3}>3</option>
                                    </Form.Select>
                                    {hasError('ca-requested-la-count') && (
                                        <Form.Control.Feedback
                                            type="invalid"
                                            id="ca-requested-la-count-error"
                                            role="alert"
                                        >
                                            This field is required.
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6} className="d-flex align-items-center">
                                <p className="text-muted mb-3">
                                    {laRatio != null
                                        ? `Your student-to-LA ratio: ${laRatio}:1`
                                        : programResponses.la
                                                .requestedLACount === ''
                                          ? "Select how many LAs you're requesting to see your estimated student-to-LA ratio."
                                          : 'Student-to-LA ratio unavailable — this section has no enrollment number on file.'}
                                </p>
                            </Col>
                        </Row>

                        <fieldset
                            className={
                                hasError('ca-la-nominee-group')
                                    ? 'mb-2 border border-danger rounded p-2'
                                    : 'mb-2'
                            }
                            id="ca-la-nominee-group"
                            tabIndex={-1}
                            aria-invalid={hasError('ca-la-nominee-group')}
                            aria-describedby={
                                hasError('ca-la-nominee-group')
                                    ? 'ca-la-nominee-group-error'
                                    : undefined
                            }
                        >
                            <Form.Label
                                as="legend"
                                className="float-none w-auto fs-6 mb-2"
                            >
                                {programResponses.la.requestedLACount > 1
                                    ? 'Will you be naming any students for these roles?'
                                    : 'Will you be naming a student for this role?'}{' '}
                                <span className="text-danger">*</span>
                            </Form.Label>
                            {LA_NOMINEE_STATUS_OPTIONS.map((opt) => (
                                <Form.Check
                                    key={opt.value}
                                    type="radio"
                                    id={`ca-la-nominee-${opt.value}`}
                                    name="ca-la-nominee"
                                    label={opt.label}
                                    checked={nomineeStatus === opt.value}
                                    onChange={() =>
                                        handleLANomineeChange(opt.value)
                                    }
                                    required
                                />
                            ))}
                            {hasError('ca-la-nominee-group') && (
                                <p
                                    id="ca-la-nominee-group-error"
                                    className="text-danger small mb-0 mt-1"
                                    role="alert"
                                >
                                    This field is required.
                                </p>
                            )}
                        </fieldset>

                        {hasLANominee && laNominees.length !== 0 ? (
                            <>
                                <StudentEmploymentRequirements headingId="ca-la-requirements-heading" />
                                <SelectedStudentsFields
                                    nominees={laNominees}
                                    requestedCount={
                                        programResponses.la.requestedLACount
                                    }
                                    onAdd={handleAddLANominee}
                                    onUpdate={updateNamedStudent}
                                    onRemove={removeNamedStudent}
                                    hasError={hasError}
                                    nomineeIdErrors={nomineeIdErrors}
                                />
                                {laTrainingSessions && (
                                    <p className="small text-muted mb-3">
                                        {trainingReminder}
                                    </p>
                                )}
                                <fieldset
                                    className={
                                        hasError('ca-la-decline-group')
                                            ? 'mb-3 border border-danger rounded p-2'
                                            : 'mb-3'
                                    }
                                    id="ca-la-decline-group"
                                    tabIndex={-1}
                                    aria-invalid={hasError(
                                        'ca-la-decline-group'
                                    )}
                                    aria-describedby={
                                        hasError('ca-la-decline-group')
                                            ? 'ca-la-decline-group-error'
                                            : undefined
                                    }
                                >
                                    <Form.Label
                                        as="legend"
                                        className="float-none w-auto fs-6 mb-2"
                                    >
                                        {/* One answer for every nominee */}
                                        {laNominees.length > 1
                                            ? 'What should happen if a nominee declines?'
                                            : 'What should happen if your nominee declines?'}{' '}
                                        <span className="text-danger">*</span>
                                    </Form.Label>
                                    {LA_NOMINEE_DECLINE_OPTIONS.map((opt) => (
                                        <Form.Check
                                            key={opt.value}
                                            type="radio"
                                            id={`ca-la-decline-${opt.value}`}
                                            name="ca-la-decline"
                                            label={opt.label}
                                            checked={
                                                programResponses.la
                                                    .nomineeDeclinePreference ===
                                                opt.value
                                            }
                                            onChange={() =>
                                                handleNomineeDeclinePreferenceChange(
                                                    opt.value
                                                )
                                            }
                                        />
                                    ))}
                                    {hasError('ca-la-decline-group') && (
                                        <p
                                            id="ca-la-decline-group-error"
                                            className="text-danger small mb-0 mt-1"
                                            role="alert"
                                        >
                                            This field is required.
                                        </p>
                                    )}
                                </fieldset>
                            </>
                        ) : nomineeStatus === 'selecting' ? (
                            <Alert variant="light" className="border">
                                No problem. The LA program will wait for you to
                                share your student&apos;s name, email, and 900
                                number once you have them.
                            </Alert>
                        ) : nomineeStatus === 'needs_help' ? (
                            <Alert variant="light" className="border">
                                Your request will go into the LA program&apos;s
                                general pool, and a coordinator will work on
                                finding a student for your course. Matching
                                isn&apos;t automatic, and we can&apos;t
                                guarantee a match.
                            </Alert>
                        ) : null}

                        <fieldset
                            className={
                                hasError('ca-la-support-use-cases-group')
                                    ? 'mb-3 border border-danger rounded p-2'
                                    : 'mb-3'
                            }
                            id="ca-la-support-use-cases-group"
                            tabIndex={-1}
                            aria-invalid={hasError(
                                'ca-la-support-use-cases-group'
                            )}
                            aria-describedby={
                                hasError('ca-la-support-use-cases-group')
                                    ? 'ca-la-support-use-cases-group-error'
                                    : undefined
                            }
                        >
                            <Form.Label
                                as="legend"
                                className="float-none w-auto fs-6"
                            >
                                How will LA support be used? (click all that
                                apply) <span className="text-danger">*</span>
                            </Form.Label>
                            {LA_SUPPORT_USE_CASE_OPTIONS.map((opt) => (
                                <Form.Check
                                    key={opt.value}
                                    type="checkbox"
                                    id={`ca-la-support-use-case-${opt.value}`}
                                    label={opt.label}
                                    checked={(
                                        programResponses.la.supportUseCases ||
                                        []
                                    ).includes(opt.value)}
                                    onChange={() =>
                                        toggleLASupportUseCase(opt.value)
                                    }
                                />
                            ))}
                            {hasError('ca-la-support-use-cases-group') && (
                                <p
                                    id="ca-la-support-use-cases-group-error"
                                    className="text-danger small mb-0 mt-1"
                                    role="alert"
                                >
                                    Select at least one option.
                                </p>
                            )}
                            {(
                                programResponses.la.supportUseCases || []
                            ).includes('other') && (
                                <div className="mt-2">
                                    <Form.Label htmlFor="ca-la-other-support-use-case">
                                        Describe the other use case{' '}
                                        <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        id="ca-la-other-support-use-case"
                                        type="text"
                                        placeholder="Describe how else LA support will be used"
                                        value={
                                            programResponses.la
                                                .otherSupportUseCase
                                        }
                                        onChange={(e) =>
                                            onProgramResponseChange(
                                                'la',
                                                'otherSupportUseCase',
                                                e.target.value
                                            )
                                        }
                                        required
                                        isInvalid={hasError(
                                            'ca-la-other-support-use-case'
                                        )}
                                        aria-invalid={hasError(
                                            'ca-la-other-support-use-case'
                                        )}
                                        aria-describedby={
                                            hasError(
                                                'ca-la-other-support-use-case'
                                            )
                                                ? 'ca-la-other-support-use-case-error'
                                                : undefined
                                        }
                                    />
                                    {hasError(
                                        'ca-la-other-support-use-case'
                                    ) && (
                                        <Form.Control.Feedback
                                            type="invalid"
                                            id="ca-la-other-support-use-case-error"
                                            role="alert"
                                        >
                                            This field is required.
                                        </Form.Control.Feedback>
                                    )}
                                </div>
                            )}
                        </fieldset>

                        <Form.Group
                            className="mb-3"
                            controlId="ca-expected-la-responsibilities"
                        >
                            <Form.Label>
                                Using the previous checklist, explain how you
                                will use your LA each week (i.e., what does a
                                typical class/session look like){' '}
                                <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={
                                    programResponses.la
                                        .expectedLAResponsibilities
                                }
                                onChange={(e) =>
                                    onProgramResponseChange(
                                        'la',
                                        'expectedLAResponsibilities',
                                        e.target.value
                                    )
                                }
                                required
                                isInvalid={hasError(
                                    'ca-expected-la-responsibilities'
                                )}
                                aria-invalid={hasError(
                                    'ca-expected-la-responsibilities'
                                )}
                                aria-describedby={
                                    hasError('ca-expected-la-responsibilities')
                                        ? 'ca-expected-la-responsibilities-error'
                                        : undefined
                                }
                            />
                            {hasError('ca-expected-la-responsibilities') && (
                                <Form.Control.Feedback
                                    type="invalid"
                                    id="ca-expected-la-responsibilities-error"
                                    role="alert"
                                >
                                    This field is required.
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>

                        <Form.Group
                            className="mb-3"
                            controlId="ca-weekly-prep-plan"
                        >
                            <Form.Label>
                                Explain how you will prepare the LA each week
                                during the required weekly prep session.{' '}
                                <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={programResponses.la.weeklyPrepPlan}
                                onChange={(e) =>
                                    onProgramResponseChange(
                                        'la',
                                        'weeklyPrepPlan',
                                        e.target.value
                                    )
                                }
                                required
                                isInvalid={hasError('ca-weekly-prep-plan')}
                                aria-invalid={hasError('ca-weekly-prep-plan')}
                                aria-describedby={
                                    hasError('ca-weekly-prep-plan')
                                        ? 'ca-weekly-prep-plan-error'
                                        : undefined
                                }
                            />
                            {hasError('ca-weekly-prep-plan') && (
                                <Form.Control.Feedback
                                    type="invalid"
                                    id="ca-weekly-prep-plan-error"
                                    role="alert"
                                >
                                    This field is required.
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>

                        <Form.Group
                            className="mb-3"
                            controlId="ca-weekly-meeting-hours"
                        >
                            <Form.Label>
                                How long are the weekly meetings?{' '}
                                <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select
                                value={String(
                                    programResponses.la.weeklyMeetingHours || ''
                                )}
                                onChange={(e) =>
                                    onProgramResponseChange(
                                        'la',
                                        'weeklyMeetingHours',
                                        e.target.value === ''
                                            ? ''
                                            : Number(e.target.value)
                                    )
                                }
                                required
                                isInvalid={hasError('ca-weekly-meeting-hours')}
                                aria-invalid={hasError(
                                    'ca-weekly-meeting-hours'
                                )}
                                aria-describedby={
                                    hasError('ca-weekly-meeting-hours')
                                        ? 'ca-weekly-meeting-hours-error'
                                        : undefined
                                }
                            >
                                <option value="">Choose how long</option>
                                {MEETING_HOUR_OPTIONS.map(
                                    ({ value, label }) => (
                                        <option
                                            key={value}
                                            value={String(value)}
                                        >
                                            {label}
                                        </option>
                                    )
                                )}
                            </Form.Select>
                            {hasError('ca-weekly-meeting-hours') && (
                                <Form.Text
                                    id="ca-weekly-meeting-hours-error"
                                    className="text-danger d-block"
                                    role="alert"
                                >
                                    Choose how long the weekly meetings are.
                                </Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group
                            className="mb-3"
                            controlId="ca-assessment-plan"
                        >
                            <Form.Label>
                                As you are the LA&apos;s day-to-day supervisor,
                                how will you evaluate and provide feedback to
                                your LA? <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={programResponses.la.assessmentPlan}
                                onChange={(e) =>
                                    onProgramResponseChange(
                                        'la',
                                        'assessmentPlan',
                                        e.target.value
                                    )
                                }
                                required
                                isInvalid={hasError('ca-assessment-plan')}
                                aria-invalid={hasError('ca-assessment-plan')}
                                aria-describedby={
                                    hasError('ca-assessment-plan')
                                        ? 'ca-assessment-plan-error'
                                        : undefined
                                }
                            />
                            {hasError('ca-assessment-plan') && (
                                <Form.Control.Feedback
                                    type="invalid"
                                    id="ca-assessment-plan-error"
                                    role="alert"
                                >
                                    This field is required.
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>

                        {needsRecruitingMessage && (
                            <Form.Group
                                className="mb-3"
                                controlId="ca-recruiting-message"
                            >
                                <Form.Label>
                                    Recruiting Message{' '}
                                    <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Text className="d-block mb-2 text-muted">
                                    Typically covers what the role involves, the
                                    time commitment, and a pitch for why the
                                    student should want it (e.g. &quot;Are you
                                    passionate about this subject and eager to
                                    deepen your understanding...&quot;).
                                </Form.Text>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={
                                        programResponses.la.recruitingMessage
                                    }
                                    onChange={(e) =>
                                        onProgramResponseChange(
                                            'la',
                                            'recruitingMessage',
                                            e.target.value
                                        )
                                    }
                                    required
                                    isInvalid={hasError(
                                        'ca-recruiting-message'
                                    )}
                                    aria-invalid={hasError(
                                        'ca-recruiting-message'
                                    )}
                                    aria-describedby={
                                        hasError('ca-recruiting-message')
                                            ? 'ca-recruiting-message-error'
                                            : undefined
                                    }
                                />
                                {hasError('ca-recruiting-message') && (
                                    <Form.Control.Feedback
                                        type="invalid"
                                        id="ca-recruiting-message-error"
                                        role="alert"
                                    >
                                        This field is required.
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                        )}
                    </Card.Body>
                </Card>
            )}

            {selected.includes('si') && (
                <Card className="mb-4 border-0 shadow-sm">
                    <Card.Header className="bg-success text-white">
                        <strong>SI - Supplemental Instruction</strong>
                    </Card.Header>
                    <Card.Body>
                        <Form.Check
                            type="checkbox"
                            id="ca-si-bottleneck"
                            label="This is a bottleneck course (high DFW, required for major)"
                            className="mb-3"
                            checked={programResponses.si.isBottleneckCourse}
                            onChange={(e) =>
                                onProgramResponseChange(
                                    'si',
                                    'isBottleneckCourse',
                                    e.target.checked
                                )
                            }
                        />
                        <Row>
                            <Col md={6}>
                                <Form.Group
                                    className="mb-3"
                                    controlId="ca-expected-sessions-per-week"
                                >
                                    <Form.Label>
                                        Expected Sessions Per Week
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        max="7"
                                        placeholder="e.g., 3"
                                        value={
                                            programResponses.si
                                                .expectedSessionsPerWeek
                                        }
                                        onChange={(e) =>
                                            onProgramResponseChange(
                                                'si',
                                                'expectedSessionsPerWeek',
                                                e.target.value
                                            )
                                        }
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Check
                            type="checkbox"
                            id="ca-si-collaborate"
                            label="Instructor is willing to collaborate with SI Leader"
                            checked={
                                programResponses.si
                                    .instructorWillingToCollaborate
                            }
                            onChange={(e) =>
                                onProgramResponseChange(
                                    'si',
                                    'instructorWillingToCollaborate',
                                    e.target.checked
                                )
                            }
                        />
                    </Card.Body>
                </Card>
            )}
        </div>
    );
}

export default CreateApplicationSection5;
